// Authoritative playback clock helpers.
//
// The room stores an ANCHOR, not a ticking currentTime:
//   - While playing, effective time is anchor + (now - updatedAt)
//   - While paused,   effective time is anchor
// All calculations use performance.now()-style monotonic offsets where
// possible (the server uses process.hrtime.bigint to avoid wall-clock jumps).

import type { Participant, PlaybackState } from "./protocol.js";

/** Drift tolerance while playing before the server issues a hard seek. */
export const PLAYING_DRIFT_THRESHOLD_SEC = 2.0;
/** Drift tolerance while paused. */
export const PAUSED_DRIFT_THRESHOLD_SEC = 0.5;
/** Client sync report interval. */
export const SYNC_REPORT_INTERVAL_MS = 5_000;
/** Server periodic state broadcast cadence (for clients that may need it). */
export const SERVER_TICK_INTERVAL_MS = 5_000;

export interface AuthoritativeAnchor {
  /** Media time at the moment of the last mutation, in seconds. */
  anchorMediaTime: number;
  /** Monotonic clock time at the moment of the last mutation, in ms. */
  anchorMonotonicMs: number;
}

export interface InternalPlaybackState extends AuthoritativeAnchor {
  src: string | null;
  isPlaying: boolean;
  pausedAt: number | null; // wall clock ms when last entered paused; null while playing
  /**
   * Who issued the pause that put us in the current paused state, or null if
   * paused for a non-attributable reason (initial state, loadSource). Kept in
   * the authoritative state rather than only on the snapshot envelope so it
   * survives ticks (which carry causedBy: null) and reaches late joiners via
   * the welcome message.
   */
  pausedBy: Participant | null;
  duration: number | null; // seconds, or null if unknown/live
  revision: number;
  sourceRevision: number;
}

export function emptyInternalState(nowMs: number): InternalPlaybackState {
  return {
    src: null,
    isPlaying: false,
    anchorMediaTime: 0,
    anchorMonotonicMs: nowMs,
    pausedAt: nowMs,
    pausedBy: null,
    duration: null,
    revision: 0,
    sourceRevision: 0
  };
}

/** Compute effective media time given the current monotonic clock. */
export function effectiveTime(
  state: Pick<InternalPlaybackState, "anchorMediaTime" | "anchorMonotonicMs" | "isPlaying">,
  nowMs: number
): number {
  if (!state.isPlaying) return state.anchorMediaTime;
  const elapsed = Math.max(0, (nowMs - state.anchorMonotonicMs) / 1000);
  return state.anchorMediaTime + elapsed;
}

export function clampTime(t: number, duration: number | null): number {
  if (!Number.isFinite(t) || t < 0) return 0;
  if (duration !== null && t > duration) return duration;
  return t;
}

export function materializePlaybackState(
  state: InternalPlaybackState,
  nowMs: number
): PlaybackState {
  const currentTime = clampTime(
    effectiveTime(state, nowMs),
    state.duration
  );
  return {
    src: state.src,
    currentTime,
    isPlaying: state.isPlaying,
    pausedAt: state.pausedAt,
    pausedBy: state.isPlaying ? null : state.pausedBy,
    duration: state.duration,
    sourceRevision: state.sourceRevision,
    revision: state.revision
  };
}

/** Compute drift (reported - expected). Positive: client is ahead. */
export function computeDrift(
  expectedTime: number,
  reportedTime: number
): number {
  return reportedTime - expectedTime;
}